import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Home, 
  BookOpen, 
  UtensilsCrossed, 
  GraduationCap, 
  MessageSquare, 
  Phone, 
  HelpCircle, 
  ChevronDown,
  Menu,
  X,
  Heart,
  Video,
  Activity,
  Headphones,
  Brain,
  FileText,
  Clock,
  Images
} from "lucide-react";

const NewNavbar = () => {
  const menuRef = useRef(null);
  const submenuRef = useRef(null);

  const menuItems = [
    { 
      id: 1, 
      menu: "HOME", 
      url: "/home",
      icon: Home
    },
    {
      id: 2,
      menu: "INTRODUCTION",
      icon: BookOpen,
      items: [
        { id: 1, name: "About", url: "/introduction", icon: Heart },
        {
          id: 2,
          name: "Understanding Diabetes",
          url: "/understanding-diabetes",
          icon: Brain
        },
        {
          id: 3,
          name: "Understanding Hypertension & CVD",
          url: "/understanding-hypertension-and-cvd",
          icon: Heart
        },
      ],
    },
    { 
      id: 4, 
      menu: "DIET CHARTS", 
      url: "/dietchart",
      icon: UtensilsCrossed
    },
    {
      id: 7,
      menu: "EDUCATION",
      icon: GraduationCap,
      items: [
        { id: 1, name: "Videos", url: "/op-videos", icon: Video },
        { id: 2, name: "Yoga Videos", url: "/yoga-videos", icon: Activity },
        { id: 3, name: "Podcast", url: "/podcasts", icon: Headphones },
        { id: 4, name: "Quiz", url: "/quiz", icon: Brain },
        { id: 5, name: "Blogs", url: "/blogs", icon: FileText },
        { id: 6, name: "Latest Updates", url: "/latest-updates", icon: Clock },
        { id: 7, name: "Image Gallery", url: "/gallery", icon: Images },
      ],
    },
    { 
      id: 8, 
      menu: "TESTIMONIALS", 
      url: "/testimonials",
      icon: MessageSquare
    },
    { 
      id: 9, 
      menu: "CONTACT US", 
      url: "/contact-us",
      icon: Phone
    },
    {
      id: 10,
      menu: "FAQ's",
      icon: HelpCircle,
      items: [
        { id: 1, name: "Diabetes", url: "/faqs-diabetes", icon: Brain },
        { id: 2, name: "Hypertension & CVD", url: "/faqs-hypertension", icon: Heart },
      ],
    },
  ];

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleSubMenu = (id) => {
    setOpenSubMenu(openSubMenu === id ? null : id);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenSubMenu(null);
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav
      ref={menuRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out ${
        scrolled 
          ? 'bg-white shadow-lg border-b border-gray-200 xl:bg-white/95 xl:backdrop-blur-md' 
          : 'bg-[#258695]'
      }`}
    >
      <div className="relative flex items-center justify-between px-4 py-5">
        {/* Logo - Restored to original position */}
        <div className="lg:absolute xl:left-30 lg:left-10 xl:top-5 lg:top-2 z-10">
          <Link to="/" className="flex items-center group">
            <img
              src="../assets/MOP.PNG"
              alt="Mission Organ Protection"
              className={`transition-all duration-300 group-hover:scale-105 ${
                scrolled 
                  ? 'w-12 md:w-14 lg:w-16 xl:w-20' 
                  : 'w-16 md:w-20 lg:w-24 xl:w-30'
              }`}
            />
          </Link>
        </div>

        {/* Desktop Menu */}
        <div className="ml-auto hidden xl:flex items-center xl:mr-32">
          <ul className="flex items-center space-x-1 lg:space-x-0">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <li key={item.id} className="relative group">
                  <div className="flex items-center">
                    <Link
                      to={item.url}
                      className={`flex items-center px-2 lg:px-3 py-2 lg:py-3 text-xs lg:text-sm font-medium rounded-lg transition-all duration-300 ease-in-out group-hover:scale-105 ${
                        scrolled 
                          ? 'text-gray-800 hover:bg-teal-600 hover:text-white' 
                          : 'text-white hover:bg-teal-700 hover:text-white'
                      }`}
                    >
                      <IconComponent className={`w-4 h-4 mr-2 ${
                        scrolled ? 'text-gray-600 group-hover:text-white' : 'text-white'
                      }`} />
                      <span className="font-semibold">{item.menu}</span>
                    </Link>
                    
                    {item.items && (
                      <div className="relative">
                        <button
                          onClick={() => toggleSubMenu(item.id)}
                          className={`p-2 ml-1 rounded-lg transition-all duration-300 ${
                            scrolled 
                              ? 'text-gray-600 hover:bg-teal-600 hover:text-white' 
                              : 'text-white hover:bg-teal-700 hover:text-white'
                          }`}
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${
                            openSubMenu === item.id ? 'rotate-180' : ''
                          }`} />
                        </button>
                        
                        {/* Dropdown Menu */}
                        <div className={`absolute top-full left-1/2  -translate-x-1/2 mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top scale-95 group-hover:scale-100 z-50`}>
                          <div className="py-2">
                            {item.items.map((subItem) => {
                              const SubIconComponent = subItem.icon;
                              return (
                                <Link
                                  key={subItem.id}
                                  to={subItem.url}
                                  className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-all duration-200 group/item"
                                >
                                  <SubIconComponent className="w-4 h-4 mr-3 text-gray-400 group-hover/item:text-teal-600" />
                                  <span className="font-medium">{subItem.name}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Mobile Menu Button */}
        <div className="xl:hidden flex items-center ml-auto">
          <button
            onClick={toggleMenu}
            className={`inline-flex items-center justify-center p-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-inset ${
              scrolled 
                ? 'text-gray-800 hover:bg-gray-100 focus:ring-gray-500' 
                : 'text-white hover:bg-white/10 focus:ring-white'
            }`}
            aria-expanded="false"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 xl:hidden ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={closeMenu}
      />

      {/* Mobile Menu Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out xl:hidden ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Mobile Menu Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <img
              src="../assets/MOP.PNG"
              alt="Mission Organ Protection"
              className="h-10 w-auto"
            />
          </div>
          <button
            onClick={closeMenu}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Menu Items */}
        <div className="py-4 overflow-y-auto h-[calc(100vh-80px)]">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <li key={item.id} className="px-4">
                  <div className="space-y-1">
                    <div
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                      onClick={() => item.items ? toggleSubMenu(item.id) : closeMenu()}
                    >
                      <Link
                        to={item.url}
                        className="flex items-center flex-1"
                        onClick={(e) => !item.items && e.stopPropagation()}
                      >
                        <IconComponent className="w-5 h-5 mr-3 text-teal-600" />
                        <span className="font-medium text-gray-900">{item.menu}</span>
                      </Link>
                      {item.items && (
                        <ChevronDown
                          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                            openSubMenu === item.id ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </div>
                    
                    {/* Mobile Submenu */}
                    {item.items && (
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          openSubMenu === item.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <ul className="ml-8 space-y-1">
                          {item.items.map((subItem) => {
                            const SubIconComponent = subItem.icon;
                            return (
                              <li key={subItem.id}>
                                <Link
                                  to={subItem.url}
                                  onClick={closeMenu}
                                  className="flex items-center p-3 rounded-lg text-gray-600 hover:bg-teal-50 hover:text-teal-700 transition-colors duration-200"
                                >
                                  <SubIconComponent className="w-4 h-4 mr-3 text-gray-400" />
                                  <span className="text-sm font-medium">{subItem.name}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default NewNavbar;
